import clsx from "clsx";
import { useEffect, useState } from "react";
import { useMyDepartment } from "../../auth/useMyDepartment";
import { NotificationBell } from "../widgets/NotificationBell";
import { useSidebarStore } from "../../store/sidebar.store";
import { NAV_ICONS } from "../icons/navIcons";
import { UserDropdown } from "../layout/UserDropdown";
import { m } from "framer-motion";
import { Avatar } from "../common/Avatar";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "../common/LanguageSwitcher";
import ThemeSwitcher from "../common/ThemeSwitcher";
import { useTranslation } from "react-i18next";

/**
 * NEW AUTH IMPORT
 */
import { useAuth } from "../../auth/AuthProvider";
import { useOrganisationSwitch } from "../../auth/OrganisationSwitchProvider";
import { OrganisationSwitcher } from "./OrganisationSwitcher";
import { staffApi } from "../../api/staffApi";

export function Topbar() {
  const { toggleOpen } = useSidebarStore();

  /**
   * NEW AUTH CONTEXT
   */
  const { logout, user, isAuthenticated } = useAuth();
  const { currentOrganisation } = useOrganisationSwitch();
  const { t } = useTranslation(["common", "topbar"]);

  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  const MenuIcon = NAV_ICONS.menu;

  /**
   * USER STATE
   */
  const [userId, setUserId] = useState<string | null>(null);
  const [userType, setUserType] = useState<"member" | "customer" | "unknown">(
    "unknown",
  );

  /**
   * USER DISPLAY DATA
   */
  // Where this person sits in the organisation. Resolved from the member
  // record because /Auth/me returns neither.
  const {
    departmentName,
    businessUnitName,
    isActive: isMemberActive,
  } = useMyDepartment();

  const [userData, setUserData] = useState({
    name: t("guestUser", { ns: "common" }),
    role: t("user", { ns: "common" }),
    email: "",
    phone: "",
    location: "Noida, UP",
    joined: "2023",
    avatarUrl: "",
    departmentName: "",
    businessUnitName: "",
  });

  /**
   * LOAD USER FROM AUTH CONTEXT
   */
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      let currentUserType: "member" | "customer" | "unknown" = "unknown";

      if (user.roles?.includes("Customer")) {
        currentUserType = "customer";
      } else {
        currentUserType = "member";
      }

      setUserType(currentUserType);

      const extractedId = user.id || null;
      setUserId(extractedId);

      setUserData((prev) => ({
        ...prev,
        name:
          user.fullName ||
          t("guestUser", { ns: "common", defaultValue: "Guest User" }),
        role:
          user.roles && user.roles.length > 0
            ? user.roles.join(", ")
            : user.isAdmin
              ? t("superAdmin", { ns: "common", defaultValue: "Super Admin" })
              : t("user", { ns: "common", defaultValue: "User" }),
        email: user.email || "",
        phone: "",
        location: "Noida, UP",
        joined: "2023",
      }));
    } catch (error) {
      console.error("Error loading auth user", error);
    }
  }, [user, isAuthenticated, t]);

  /**
   * LOAD PROFILE PICTURE
   * The JWT-derived auth user carries no photo — fetch it separately from
   * the member record so the header/profile-panel avatars aren't stuck on
   * initials for users who do have one uploaded.
   */

  /**
   * LOAD PROFILE PICTURE
   */
  useEffect(() => {
    if (!userId || userType !== "member") return;

    let cancelled = false;

    staffApi
      .searchStaff({
        ids: [userId],
        pageSize: 1,
        pageIndex: 0,
        // Deliberately no groupIds here. The backend only applies a
        // groupId filter for app admins when groupIds is non-empty
        // (MemberSearchRepository.BuildFilterQuery) — an admin's own
        // account may not belong to the org currently switched into, so
        // pinning groupIds to the current org would AND against the ids
        // filter and exclude it. The ids filter alone already scopes this
        // to exactly one record.
      })
      .then(async (res) => {
        if (cancelled) return;

        const staffMember = res?.data?.[0];
        if (!staffMember) return;

        let picUrl = "";

        if (staffMember.profilePicUrl) {
          picUrl = staffMember.profilePicUrl;
        } else if (staffMember.profilePic?.startsWith("http")) {
          picUrl = staffMember.profilePic;
        } else if (staffMember.profilePic) {
          try {
            // The picture lives in the member's own organisation, which for
            // an admin may not be the one switched into.
            const mediaRes = await staffApi.getMediaLink(
              [staffMember.profilePic],
              1,
              staffMember.groupId,
            );
            picUrl = mediaRes.links?.[0]?.link || "";
          } catch (err) {
            console.error("Failed to resolve profile pic media link:", err);
          }
        }

        if (!cancelled && picUrl) {
          setUserData((prev) => ({ ...prev, avatarUrl: picUrl }));
        }
      })
      .catch((error) => {
        console.error("Failed to load profile picture", error);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, userType]);
  /**
   * EDIT PROFILE
   */
  function onEditProfile() {
    if (!userId) {
      console.error("User ID not found in Auth context!");
      return;
    }

    setProfileOpen(false);

    if (userType === "customer") {
      navigate(`/customers/${userId}`);
    } else if (userType === "member") {
      navigate(`/admin/staff/${userId}`);
    }
  }
  /**
   * SCROLL EFFECT
   */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleProfilePicUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ avatarUrl: string }>;
      if (customEvent.detail && customEvent.detail.avatarUrl !== undefined) {
        setUserData((prev) => ({
          ...prev,
          avatarUrl: customEvent.detail.avatarUrl,
        }));
      }
    };

    window.addEventListener("profilePicUpdated", handleProfilePicUpdate);

    return () => {
      window.removeEventListener("profilePicUpdated", handleProfilePicUpdate);
    };
  }, []);

  return (
    <>
      <header
        className={`
          sticky top-0 z-50 w-full
          transition-all duration-300
          backdrop-blur
          border-b border-[rgb(var(--border))]
          bg-[rgb(var(--bg))]
          ${scrolled ? "shadow-md" : ""}
        `}
      >
        <div
          className={`
            px-3 sm:px-4 md:px-6
            flex items-center justify-between
            transition-all duration-300
            ${scrolled ? "h-12" : "h-14"}
          `}
        >
          {/* LEFT SECTION (BRAND LOGO + COMPANY NAME + TEXT TAGLINE BELOW LOGO) */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 overflow-hidden">
            {/* MOBILE MENU BUTTON */}
            <button
              onClick={toggleOpen}
              className="
              md:hidden shrink-0 p-2 rounded-md
              border border-[rgb(var(--border))]
              bg-[rgb(var(--card))]
              hover:bg-[rgb(var(--card-muted))]
              text-[rgb(var(--text-accent))]
              "
              aria-label={t("openMenu", {
                ns: "topbar",
                defaultValue: "Open Menu",
              })}
            >
              <MenuIcon size={18} />
            </button>

            {/* LOGO, COMPANY NAME AND TAGLINE CONTAINER */}
            <div
              className="flex items-center gap-2 sm:gap-3 min-w-0 cursor-pointer"
              onClick={() => navigate("/")}
            >
              {/* LOGO IMAGE (Increased height dimensions) */}
              {/* Phones get the brand mark alone (cropped from the official
                  logo, public/pactline-mark.png): the full wordmark left the
                  company name ~3 characters of room. */}
              <img
                src="/pactline-mark.png"
                alt={t("logoAlt", { ns: "topbar", defaultValue: "Pactline Logo" })}
                width={128}
                height={128}
                className="h-9 w-auto shrink-0 object-contain sm:hidden"
              />
              <img
                src="/pactline.png"
                alt={t("logoAlt", {
                  ns: "topbar",
                  defaultValue: "Pactline Logo",
                })}
                className="hidden sm:block h-10 w-auto shrink-0 object-contain"
              />

              {/* LOGGED IN COMPANY NAME & TAGLINE */}
              <div className="flex min-w-0 flex-col items-start justify-center">
                {/* Phones: wrap to two lines rather than clip the name; the
                    single-line truncate only applies once there is room. */}
                {currentOrganisation?.name && (
                  <span
                    title={currentOrganisation.name}
                    className={clsx(
                      "text-xs sm:text-sm font-bold text-[rgb(var(--text-primary))] tracking-tight leading-tight line-clamp-2 break-words max-w-[9rem] sm:line-clamp-none sm:truncate sm:max-w-xs",
                      // Super admins get the organisation switcher, which already names the org; on phones the duplicate only squeezes the bar.
                      user?.isAppAdmin && "hidden sm:block",
                    )}
                  >
                    {currentOrganisation.name}
                  </span>
                )}

                {!scrolled && (
                  <p className="hidden sm:block max-w-full truncate text-[11px] text-[rgb(var(--text-muted))] leading-tight font-medium tracking-wide mt-0.5">
                    {t("tagline", {
                      ns: "topbar",
                      defaultValue:
                        "Contracts Simplified | Growth Amplified | Trust Fortified",
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* SUPER ADMIN ORGANISATION SWITCHER */}
            {user?.isAppAdmin && <OrganisationSwitcher />}

            {/* LANGUAGE */}
            <LanguageSwitcher />

            {/* THEME */}
            <ThemeSwitcher />

            <NotificationBell />

            {/* USER PROFILE AVATAR */}
            <m.button
              layoutId="avatarMorph"
              onClick={() => setProfileOpen(true)}
              title={t("topbar:yourProfile")}
              aria-label={t("topbar:yourProfile")}
              className="w-9 h-9 shrink-0 rounded-full overflow-hidden border border-[rgb(var(--border))] bg-[rgb(var(--card))] flex items-center justify-center"
            >
              <Avatar
                src={userData.avatarUrl}
                label={userData.name}
                size={36}
              />
            </m.button>
          </div>
        </div>
      </header>

      {/* USER PROFILE MENU DROPDOWN */}
      <UserDropdown
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onLogout={() => {
          logout();
          setProfileOpen(false);
        }}
        onEditProfile={onEditProfile}
        data={{
          ...userData,
          departmentName: departmentName || "",
          businessUnitName: businessUnitName || "",
          // null while the member record is still resolving, which the panel
          // renders as no chip at all rather than as "Inactive".
          isActive: isMemberActive ?? undefined,
        }}
      />
    </>
  );
}
